import React from 'react';
import BlockContent from '@/components/BlockContent';
import Image from 'next/image';
import { imageUrlBuilder } from '@/utils/helpers';
import styles from './styles.module.css';
import gutterStyles from '@/styles/gutter.module.css';

interface RichTextCell {
  _type: 'richTextCell';
  _key: string;
  heading?: string;
  content?: Record<string, unknown>[];
}

interface BannerCell {
  _type: 'bannerCell';
  _key: string;
  image?: { image?: { asset?: { _id?: string; url: string } }; alt?: string };
  aspectRatio?: string;
  caption?: string;
}

interface StatsCell {
  _type: 'statsCell';
  _key: string;
  items?: Array<{ _key: string; value: string; label: string }>;
}

interface QuoteCell {
  _type: 'quoteCell';
  _key: string;
  quote: string;
  author?: string;
  role?: string;
}

interface MetaListCell {
  _type: 'metaListCell';
  _key: string;
  heading: string;
  items?: string[];
}

type Cell = RichTextCell | BannerCell | StatsCell | QuoteCell | MetaListCell;

const RATIO_PAD: Record<string, string> = {
  '21:9': '42.86%',
  '16:9': '56.25%',
  '4:3': '75%',
  '1:1': '100%',
};

const RATIO_COLS: Record<string, string> = {
  '50/50': '1fr 1fr',
  '60/40': '3fr 2fr',
  '40/60': '2fr 3fr',
  '70/30': '7fr 3fr',
  '30/70': '3fr 7fr',
};

interface FlexRowProps {
  title?: string;
  columns?: 1 | 2 | 3 | 4;
  ratio?: string;
  cells?: Cell[];
  bgColor?: { hex: string };
  bgGradient?: {
    gradientDirection: string;
    colorStart: { hex: string };
    colorStop: { hex: string };
  };
  _key?: string;
  wideGutter?: boolean;
}

function RichTextCellView({ heading, content }: RichTextCell) {
  return (
    <div className={`mx-auto max-w-3xl ${styles.richText}`}>
      {heading && <h3 className="mb-4 text-xl font-bold text-a-navy">{heading}</h3>}
      {content && <BlockContent content={content} />}
    </div>
  );
}

function BannerCellView({ image, aspectRatio, caption }: BannerCell) {
  if (!image?.image?.asset) return null;

  const paddingTop = aspectRatio && RATIO_PAD[aspectRatio] ? RATIO_PAD[aspectRatio] : '56.25%';
  const imgSrc = imageUrlBuilder.image(image.image.asset).width(800).auto('format').quality(80).url();

  return (
    <div>
      <div className="relative w-full overflow-hidden" style={{ paddingTop }}>
        <Image
          src={imgSrc}
          alt={image.alt || ''}
          fill
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="object-contain"
        />
      </div>
      {caption && <p className="mt-2 text-sm text-a-text-muted">{caption}</p>}
    </div>
  );
}

function StatsCellView({ items }: StatsCell) {
  if (!items || items.length === 0) return null;

  const colClass = items.length === 1 ? 'grid-cols-1' : items.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className={`grid ${colClass} gap-4`}>
      {items.map((stat) => (
        <div key={stat._key} className="rounded-lg bg-a-off-white p-6">
          <p className="mb-1 text-4xl font-bold text-a-navy">{stat.value}</p>
          <p className="text-sm text-a-text-secondary">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

function QuoteCellView({ quote, author, role }: QuoteCell) {
  return (
    <blockquote className="border-t-2 border-a-orange pt-6">
      <span className="block text-4xl font-bold leading-none text-a-orange">&ldquo;</span>
      <p className="mt-2 text-xl leading-relaxed text-a-navy">{quote}</p>
      {(author || role) && (
        <footer className="mt-4 text-sm">
          {author && <span className="font-bold text-a-navy">{author}</span>}
          {author && role && <span> </span>}
          {role && <span className="text-a-orange">{role}</span>}
        </footer>
      )}
    </blockquote>
  );
}

function MetaListCellView({ heading, items }: MetaListCell) {
  return (
    <div>
      <h3 className="mb-4 text-2xl font-light text-a-navy">{heading}</h3>
      {items && items.length > 0 && (
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li key={`${item}-${i}`} className="text-sm text-a-orange">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function renderCell(cell: Cell) {
  switch (cell._type) {
    case 'richTextCell':
      return <RichTextCellView {...cell} />;
    case 'bannerCell':
      return <BannerCellView {...cell} />;
    case 'statsCell':
      return <StatsCellView {...cell} />;
    case 'quoteCell':
      return <QuoteCellView {...cell} />;
    case 'metaListCell':
      return <MetaListCellView {...cell} />;
    default:
      return null;
  }
}

const FlexRow: React.FC<FlexRowProps> = ({ cells, columns, ratio, bgColor, bgGradient, wideGutter }) => {
  const gridTemplate =
    columns === 2 && ratio && RATIO_COLS[ratio]
      ? RATIO_COLS[ratio]
      : `repeat(${columns ?? cells?.length ?? 1}, minmax(0, 1fr))`;

  const background =
    bgColor?.hex ||
    (bgGradient?.colorStart?.hex && bgGradient?.colorStop?.hex
      ? `linear-gradient(${bgGradient.gradientDirection}, ${bgGradient.colorStart.hex} 0%, ${bgGradient.colorStop.hex} 100%)`
      : undefined);

  const visibleCells = (cells || []).filter((cell) => {
    if (cell._type === 'bannerCell') return !!(cell as BannerCell).image?.image?.asset;
    if (cell._type === 'richTextCell') {
      const rc = cell as RichTextCell;
      return !!(rc.content?.length || rc.heading);
    }
    if (cell._type === 'statsCell') return !!(cell as StatsCell).items?.length;
    return true;
  });

  if (visibleCells.length === 0) return null;

  return (
    <section className="w-full py-6" style={background ? { background } : undefined}>
      <div className={`mx-auto w-full max-w-a-wide ${wideGutter ? gutterStyles.gutter : 'px-6 sm:px-8'}`}>
        <div
          className={`grid grid-cols-1 gap-8 items-start ${styles.flexGrid}`}
          style={{ '--flex-cols': gridTemplate } as React.CSSProperties}
        >
          {visibleCells.map((cell) => (
            <div key={cell._key}>{renderCell(cell)}</div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FlexRow;
